/**
 * This module provides semantic accessors to the membership data to clearly
 * express the membership status.
 *
 * In this module:
 *
 * - Product - is access related. What can you see. The thing you have.
 *   (eg, an active premium)
 *   - ProductSource - is where your product comes from; or what kind of product it is
 *     (eg, gifted, complimentary, investor)
 * - Subscription - is your payment: what amount, what frequency,
 *
 * Together, these constitute your Membership. Membership UIs will need to contain
 * details of access or payment or both, depending on context.
 *
 * Distinguishing facts, between product and subscription:
 *
 * 1. Subscriptions are canceled, paused or expire. Products are not canceled or paused.
 * 2. Product access expires. Subscriptions also expire, in the case of uncorrected failed payment.
 *
 * Not in this module:
 *
 * There are no UI-specific rules here. Those are in the UI component modules.
 */
import { Membership, MembershipSubscription } from './MembershipService'

export function isMember(membership: Membership | null | undefined): boolean {
  return !!membership
}

/**
 * Product access says whether you can access features of the Guild.
 * It is unrelated to a subscription or payment.
 *
 * No access is the same as expired.
 *
 * Any status that's not ACTIVE is considered expired. Primary product
 * (the only one that really matters for membership status), will only
 * ever be ACTIVE or EXPIRED.
 */
export function hasAccess(membership: Membership | null | undefined): boolean {
  // NOTE - same as membership.active, but this is more clear where that derives from
  return !!membership && membership.primaryProduct.accessStatus === 'ACTIVE'
}

export function isAccessGift(membership: Membership | null | undefined): boolean {
  return !!membership?.primaryProduct.isGift
}

export function isAccessInvestor(membership: Membership | null | undefined): boolean {
  // TODO: future: products with tier INVESTOR are expected to go away. Then this combo check can be removed.
  return !!membership?.isInvestor && (membership.primaryProduct ? membership.primaryProduct.tier === 'INVESTOR' : true)
}

export function isAccessComplimentary(membership: Membership | null | undefined): boolean {
  return !!membership && !isAccessInvestor(membership) && !!membership?.primaryProduct?.grantId
}

export function isSubCanceled(membership: Membership | null | undefined): boolean {
  return membership?.primaryProduct?.subscription?.status === 'CANCELLED'
}

export function isSubPaused(membership: Membership | null | undefined): boolean {
  return membership?.primaryProduct?.subscription?.status === 'PAUSED'
}

export function isSubExpired(membership: Membership | null | undefined): boolean {
  return membership?.primaryProduct?.subscription?.status === 'EXPIRED'
}

export function isSubPaymentFailing(membership: Membership | null | undefined): boolean {
  return membership?.primaryProduct?.subscription?.status === 'PAYMENT_FAILING'
}

/**
 * Checks if a subscription is stuck in an expired provisional state.
 * This happens when a purchase was initiated but the webhook confirmation never arrived
 * and the provisional access window has since expired.
 *
 * The backend retains PROVISIONAL status after period-end (analogous to how PAUSED
 * subscriptions are not transitioned to EXPIRED), making this check unambiguous.
 */
export function isExpiredProvisional(membership: Membership | null | undefined): boolean {
  return !hasAccess(membership) && isSubProvisional(membership?.primaryProduct?.subscription)
}

/**
 * Checks if a subscription is in a manageable state (can be paused or cancelled).
 * ACTIVE, PAUSED, and PAYMENT_FAILING subscriptions have actions the user can take.
 */
export function isSubManageable(subscription: MembershipSubscription | null | undefined): boolean {
  return !!subscription && ['ACTIVE', 'PAUSED', 'PAYMENT_FAILING'].includes(subscription.status)
}

/**
 * Checks if a subscription is provisional — confirmed by checkout, awaiting webhook confirmation.
 * Provisional subscriptions show temporary grant values (BASIC/NEVER_EXPIRES) that should not
 * be displayed to the user as their actual plan name.
 */
export function isSubProvisional(subscription: MembershipSubscription | null | undefined): boolean {
  return subscription?.status === 'PROVISIONAL'
}

/**
 * Checks if a membership is in a state where purchase/checkout should be blocked.
 * Covers paused subscriptions (user must resume first) and provisional subscriptions
 * (purchase is already in-flight, awaiting webhook confirmation).
 */
export function isSubPurchaseBlocked(membership: Membership | null | undefined): boolean {
  return isSubPaused(membership) || isSubProvisional(membership?.primaryProduct?.subscription)
}
